import { QUERY_KEYS } from "@/libraries/constant";
import { askHemiService } from "@/services/askhemi";
import type { ResError } from "@/types";
import type {
	AskHemiShare,
	CaseAskHemiProfile,
	WoundAnalysis,
} from "@/types/askhemi";
import { queryOptions } from "@tanstack/react-query";

export const getAskHemiShareQueryOptions = (id: number | string) =>
	queryOptions<AskHemiShare, ResError>({
		queryKey: [QUERY_KEYS.GET_ASKHEMI_SHARE, String(id)],
		queryFn: ({ signal }) => askHemiService.getShare(id, { signal }),
		// Photo URLs are presigned, so keep them reasonably fresh.
		staleTime: 1000 * 60 * 5,
		gcTime: 1000 * 60 * 10,
		// 410 (revoked/expired) and 4xx are final, don't hammer the server.
		retry: (count, error) =>
			!(error.response?.status && error.response.status < 500) && count < 2,
	});

export const getCareChatQueryOptions = (caseId: number | string) =>
	queryOptions({
		queryKey: [QUERY_KEYS.GET_CARE_CHAT, String(caseId)],
		queryFn: ({ signal }) => askHemiService.getCareChat(caseId, { signal }),
		// Poll so a chat ended (or reopened) elsewhere shows up here.
		refetchInterval: 30000,
	});

export const getCareChatMessagesQueryOptions = (
	caseId: number | string,
	options?: { refetchInterval?: number | false; enabled?: boolean },
) =>
	queryOptions({
		queryKey: [QUERY_KEYS.GET_CARE_CHAT_MESSAGES, String(caseId)],
		queryFn: ({ signal }) =>
			askHemiService.getCareChatMessages(caseId, undefined, { signal }),
		...options,
	});

export const getWoundAnalysisQueryOptions = (id: number | string | null) =>
	queryOptions<WoundAnalysis, ResError>({
		queryKey: [QUERY_KEYS.GET_WOUND_ANALYSIS, String(id)],
		queryFn: ({ signal }) =>
			askHemiService.getWoundAnalysis(id as number, { signal }),
		enabled: !!id,
		refetchInterval: (query) => {
			const status = query.state.data?.status;
			return status === "pending" || status === "processing" ? 3000 : false;
		},
		refetchIntervalInBackground: true,
	});

export const listWoundAnalysesQueryOptions = (caseId: number | string) =>
	queryOptions({
		queryKey: [QUERY_KEYS.GET_WOUND_ANALYSES, String(caseId)],
		queryFn: ({ signal }) =>
			askHemiService.listWoundAnalyses(caseId, { signal }),
	});

export const getCaseAskHemiProfileQueryOptions = (
	caseId: number | string,
	options?: { enabled?: boolean },
) =>
	queryOptions<CaseAskHemiProfile, ResError>({
		queryKey: [QUERY_KEYS.GET_CASE_ASKHEMI_PROFILE, String(caseId)],
		queryFn: ({ signal }) =>
			askHemiService.getCaseAskHemiProfile(caseId, { signal }),
		enabled: !!caseId && (options?.enabled ?? true),
		staleTime: 1000 * 60 * 5,
		gcTime: 1000 * 60 * 10,
		retry: (count, error) =>
			!(error.response?.status && error.response.status < 500) && count < 2,
	});
